import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import {
  useCultivationFormData,
  useCultivationFormFieldData,
  useGetCultivationFormData,
  useUpdateCultivationFormData
} from "../../../../api/farmGate";
import { InputText } from "../../../../components";
import CommonSplitContainer from "../../../../components/commonSplitContainer/CommonSplitContainer";
import MainBottomButtonView from "../../../../components/mainBottomButtonView/MainBottomButtonView";
import CultivarField from "./components/CultivarField";
import Fertilizer from "./components/Fertilizer";
import Fields from "./components/Fields";
import FuelUse from "./components/FuelUse";
import Irrigation from "./components/Irrigation";
import LandUse from "./components/LandUse";
import Pesticides from "./components/Pesticides";

import { successMessage } from "../../../../helpers/ErrorHandler";
import { validateCharacterLength } from "../../../../utils/utils";
import "./styles/CultivarForm.scss";
interface CultivarFormProps {
  supplierId: string;
  ingredientId: string;
  farmGateId: string;
  nodeItem: any;
  updateDrawingNode: (node: any, farmGateName?: string) => void;
  setNodeItem: any;
  productId: any;
  salesUnitId: any;
}
interface SaveDocumentData {
  saveDocumentAndNote(id: any): void;
  deleteComment(): void;
}

const CULTIVAR_INIT_STEP = "CULTIVAR";

const CultivarSimulationForm: React.FC<CultivarFormProps> = ({
  supplierId,
  ingredientId,
  farmGateId,
  nodeItem,
  productId,
  salesUnitId,
  updateDrawingNode
}) => {
  const cultivationRef = useRef<any>(null);
  const fieldRef = useRef<any>(null);
  const fertilizerRef = useRef<any>(null);
  const pesticideRef = useRef<any>(null);
  const irrigationRef = useRef<any>(null);
  const fuelUseRef = useRef<any>(null);
  const landUseRef = useRef<any>(null);
  const ref = useRef<SaveDocumentData>(null);

  const { data: currentFormData } = useGetCultivationFormData(farmGateId);
  const [currentDataType, setCurrentDataType] = useState(currentFormData?.dataType || "Supplier");
  const [isCultivarValid, setIsCultivarValid] = useState(false);
  const [isFieldDataValid, setIsFieldDataValid] = useState(false);

  const {
    data: currentOriginalFormData,
    status: formFetchStatus,
    fetchStatus: formFetchState
  } = useGetCultivationFormData(currentFormData?.originalId);

  const {
    mutate: cultivationFormFieldDataMutate,
    isLoading: isCultivationFormFieldDataMutateLoading
  } = useCultivationFormFieldData();

  const { mutate: cultivationFormDataMutate, isLoading: isCultivarFormDataMutateLoading } =
    useCultivationFormData();

  const { mutate: updateCultivationDataMutate, isLoading: isUpdateCultivationDataMutateLoading } =
    useUpdateCultivationFormData();

  const updateNodeItem = useCallback(
    (nodeItem: any, farmGateId: string) => {
      return {
        ...nodeItem,
        data: {
          ...nodeItem?.data,
          reference: {
            farmGateId,
            supplierId,
            ingredientId
          }
        }
      };
    },
    [supplierId, ingredientId]
  );

  const values = useMemo(() => {
    return {
      farmGateName: currentFormData?.farmGateName ?? ""
    };
  }, [currentFormData]);

  const {
    register,
    formState: { errors, isValid },
    handleSubmit
  } = useForm({ defaultValues: values, values: values, mode: "all" });

  const setFormDataForField = (farmGateId: string, values: any) => {
    const fieldData = fieldRef?.current?.getFieldData();
    const harvestSeason = fieldData.harvestSeason;
    const fertilizerTableList = fertilizerRef?.current?.getFretilizerData() || [];
    const pesticideTableList = pesticideRef?.current?.getPesticideData() || [];
    const fertilizerList = fertilizerTableList.map((item: any) => {
      return { id: item.id, fertilizerQuantity: item.fertilizerQuantity };
    });
    const pesticideList = pesticideTableList.map((item: any) => {
      return { id: item.id, pesticideQuantity: item.pesticideQuantity };
    });
    const irrigations = irrigationRef?.current?.getIrrigationData() || [];
    const fuelUse = fuelUseRef?.current?.getFuelUse() || [];
    const landUsage = landUseRef?.current?.getLandUseData() || [];
    return {
      supplierId,
      ingredientId,
      farmGateId,
      yield: values.yield,
      growthArea: values.growthArea,
      harvestYear: values.harvestYear,
      harvestSeason,
      fertilizerList,
      pesticideList,
      irrigations,
      fuelUse,
      landUsage
    };
  };

  const updateNode = async (cFormData: any, values: any, farmGateId: any) => {
    try {
      ref?.current?.saveDocumentAndNote(farmGateId);
      const cultivationFieldFormValues = setFormDataForField(farmGateId, values);
      cultivationFormFieldDataMutate(cultivationFieldFormValues);
      const updatedNodeItem = updateNodeItem(nodeItem, farmGateId);
      updateDrawingNode(updatedNodeItem, cFormData.farmGateName);
    } catch (error) {
      console.error("Error saving document and note:", error);
    }
  };

  const isLoading =
    isCultivarFormDataMutateLoading ||
    isCultivationFormFieldDataMutateLoading ||
    isUpdateCultivationDataMutateLoading;

  const onSubmitHandler = useCallback(
    async (values: any) => {
      const cultivationData = await cultivationRef?.current?.getCultivationData();
      if (!cultivationData) return;

      const fieldData = await fieldRef?.current?.getFieldData();
      if (!fieldData) return;

      const landUsageChangeStatus = landUseRef?.current?.getLanduseStatus() || "no";
      const cultivation = {
        ...values,
        supplierId,
        ingredientId,
        processing: currentFormData?.processing || [],
        packaging: currentFormData?.packaging || [],
        internalTransportation: currentFormData?.internalTransportation || [],
        userCompletedStage: CULTIVAR_INIT_STEP,
        landUsageChange: landUsageChangeStatus,
        landUsage: currentFormData?.landUsage || [],
        sourceId: salesUnitId,
        ...cultivationData,
        ...fieldData
      };

      if (!farmGateId) {
        // new farm gate
        cultivationFormDataMutate(
          { productId, salesUnitId, ...cultivation, changeStatus: true },
          {
            onSuccess(data: any) {
              updateNode(cultivation, values, data._id);
              successMessage("Cultivation created successfully");
            }
          }
        );
      } else {
        // update farm gate
        updateCultivationDataMutate(
          {
            farmGateId,
            productId,
            salesUnitId,
            ...cultivation,
            changeStatus: true
          },
          {
            onSuccess() {
              updateNode(cultivation, values, farmGateId);
              successMessage("Cultivation updated successfully");
            }
          }
        );
        return values;
      }
    },
    [farmGateId]
  );

  const isSaveEnabled = isValid && isCultivarValid && isFieldDataValid;

  if (formFetchStatus === "loading" && formFetchState === "fetching") {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const farmGateNameDisable = !nodeItem?.data?.reference?.farmGateId ? false : true;

  return (
    <>
      <Dimmer active={isLoading} inverted>
        <Loader />
      </Dimmer>
      <div>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <CommonSplitContainer
            currentHeight={0.87}
            topChild={
              <div>
                <div>
                  <Grid columns={2}>
                    <Grid.Row>
                      <Grid.Column>
                        <InputText
                          labelName={"Farm Gate Name*"}
                          name="farmGateName"
                          register={register}
                          errorMessage="Farm Gate Name is required"
                          errors={errors.farmGateName}
                          required
                          disabled={farmGateNameDisable}
                          validateHandle={(value: any) => {
                            validateCharacterLength(
                              value,
                              50,
                              "Farm Gate Name cannot exceed 50 characters"
                            );
                          }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </div>
                <CultivarField
                  ref={cultivationRef}
                  currentFormData={currentFormData}
                  currentDataType={setCurrentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                  onValidityChange={setIsCultivarValid}
                />
                <Fields
                  ref={fieldRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                  onValidityChange={setIsFieldDataValid}
                />
                <Fertilizer
                  ref={fertilizerRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                />
                <Pesticides
                  ref={pesticideRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                />
                <Irrigation
                  ref={irrigationRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                />
                <FuelUse
                  ref={fuelUseRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                />
                <LandUse
                  ref={landUseRef}
                  currentFormData={currentFormData}
                  currentDataType={currentDataType}
                  currentOriginalFormData={currentOriginalFormData}
                />
              </div>
            }
            bottomChild={
              <MainBottomButtonView
                saveButtonStatus={true}
                saveTitle={farmGateId ? "Update" : "Save"}
                type="submit"
                saveDisable={!isSaveEnabled}
              />
            }
          />
        </form>
      </div>
    </>
  );
};

export default CultivarSimulationForm;
